// services/emailService.js - Comprehensive email management service
import { get, post, put, del } from '@aws-amplify/api-rest';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'CourseBookingApi';
console.log("✅ API_NAME IS NOW:", API_NAME);

// Helper to get auth headers (same as courseService)
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper (same as courseService)
const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();

    // UNWRAP {response: Promise, ...}
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }

    // Parse fetch Response
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }

    // Handle ReadableStream (Amplify/fetch)
    if (response && response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      let chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(...value);
      }
      const text = new TextDecoder().decode(new Uint8Array(chunks));
      try {
        response = { ...response, ...JSON.parse(text) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    // If .body is a string, parse it and merge (the classic Lambda proxy pattern)
    if (response && typeof response.body === 'string') {
      try {
        response = { ...response, ...JSON.parse(response.body) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    return response;
  } catch (error) {
    console.error(`API Error in ${apiCall.name}:`, error);

    // Check if it's an auth error
    if (error.statusCode === 401 || error.name === 'NotAuthorizedException') {
      console.error('Authentication error - user may need to sign in again');
    }

    // Don't return mock data - let the error propagate so we can fix real issues
    throw error;
  }
};

// Mock data for development
const getMockEmailData1 = (apiName) => {
  const mockData = {
    getEmailTemplates: [
      {
        templateId: 'template-1',
        name: 'Welcome Email',
        type: 'welcome',
        subject: 'Welcome to our courses!',
        active: true,
        createdAt: new Date().toISOString()
      },
      {
        templateId: 'template-2',
        name: 'Payment Reminder',
        type: 'payment_reminder',
        subject: 'Payment reminder for your course',
        active: true,
        createdAt: new Date().toISOString()
      }
    ],
    getEmailTemplate: {
      templateId: 'template-1',
      name: 'Welcome Email',
      type: 'welcome',
      subject: 'Welcome to {{courseName}}!',
      htmlContent: '<h1>Welcome {{studentName}}!</h1><p>Thank you for enrolling in {{courseName}}.</p>',
      variables: ['studentName', 'courseName'],
      active: true
    },
    getEmailHistory: {
      emails: [
        {
          emailId: 'admin/email-1',
          templateId: 'template-1',
          recipient: 'student@example.com',
          subject: 'Welcome to React Fundamentals!',
          status: 'delivered',
          sentAt: new Date().toISOString()
        }
      ],
      totalCount: 1,
      pagination: {}
    },
    getEmailAnalytics: {
      totalSent: 150,
      deliveryRate: 98.5,
      openRate: 65.2,
      clickRate: 12.8,
      bounceRate: 1.5,
      topTemplates: [],
      dailyStats: []
    }
  };

  return mockData[apiName] || { success: true };
};

// ===== EMAIL TEMPLATE MANAGEMENT =====

/**
 * Get all email templates with optional filtering
 * @param {Object} filters - Optional filters (type, search, etc.)
 * @returns {Promise<Array>} List of email templates
 */
export const getEmailTemplates = (filters = {}) => handleApiCall(async function getEmailTemplates() {
  console.log('Fetching email templates with filters:', filters);

  const queryParams = new URLSearchParams();
  if (filters.type) queryParams.append('type', filters.type);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.active !== undefined) queryParams.append('active', filters.active);

  const queryString = queryParams.toString();
  const path = queryString ? `admin/email/templates?${queryString}` : 'admin/email/templates';

  let response = await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });

  console.log('Raw getEmailTemplates response:', response);

  // Handle Promise response that Amplify sometimes returns
  if (response && response.response && typeof response.response.then === 'function') {
    console.log('Response has Promise, awaiting it...');
    response = await response.response;
    console.log('Promise resolved to:', response);
  }

  // Handle ReadableStream body
  if (response && response.body && typeof response.body.json === 'function') {
    console.log('Response has ReadableStream body, parsing JSON...');
    response = await response.body.json();
    console.log('JSON parsed to:', response);
  }

  // Handle different response formats
  let templates = [];
  if (response && response.templates) {
    console.log('Found templates in response.templates:', response.templates);
    templates = response.templates;
  } else if (Array.isArray(response)) {
    console.log('Response is direct array:', response);
    templates = response;
  } else {
    console.log('No templates found, returning empty structure. Response was:', response);
    templates = [];
  }

  // Map templateType back to type for frontend compatibility
  templates = templates.map(template => ({
    ...template,
    type: template.templateType || template.type || 'custom',
    htmlContent: template.htmlBody || template.htmlContent,
    textContent: template.textBody || template.textContent
  }));

  return { templates, count: templates.length };
});

/**
 * Get a specific email template by ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Email template details
 */
export const getEmailTemplate = (templateId) => handleApiCall(async function getEmailTemplate() {
  console.log('Fetching email template:', templateId);

  return await get({
    apiName: API_NAME,
    path: `admin/email/templates/${templateId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get an email template by type
 * @param {string} templateType - Template type (e.g., 'invoice', 'welcome')
 * @returns {Promise<Object>} Email template details
 */
export const getEmailTemplateByType = (templateType) => handleApiCall(async function getEmailTemplateByType() {
  console.log('Fetching email template by type:', templateType);

  return await get({
    apiName: API_NAME,
    path: `admin/email/templates/type/${templateType}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Initialize master email templates
 * @returns {Promise<Object>} Initialization result
 */
export const initializeMasterTemplates = () => handleApiCall(async function initializeMasterTemplates() {
  console.log('Initializing master email templates');

  return await post({
    apiName: API_NAME,
    path: 'admin/email/templates/initialize',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Create a new email template
 * @param {Object} templateData - Template data (name, type, subject, htmlContent, etc.)
 * @returns {Promise<Object>} Created template
 */
export const createEmailTemplate = (templateData) => handleApiCall(async function createEmailTemplate() {
  console.log('Creating email template with data:', templateData);

  // Validate required fields
  const requiredFields = ['name', 'type', 'subject', 'htmlContent'];
  for (const field of requiredFields) {
    if (!templateData[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  // Extract variables from HTML content
  const variables = extractVariablesFromTemplate(templateData.htmlContent);

  // Convert htmlContent to htmlBody and generate textBody
  // Also map 'type' to 'templateType' for backend compatibility
  const payload = {
    ...templateData,
    templateType: templateData.type || templateData.templateType || 'custom',
    htmlBody: templateData.htmlContent || templateData.htmlBody,
    textBody: templateData.textContent || templateData.textBody ||
      templateData.htmlContent?.replace(/<[^>]*>/g, '').substring(0, 1000) || 'Email content', // Strip HTML tags for text version
    variables,
    createdAt: new Date().toISOString(),
    active: templateData.active !== undefined ? templateData.active : true
  };
  // Remove duplicate 'type' field if it exists
  delete payload.type;

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/templates',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  console.log('Raw createEmailTemplate response:', response);
  console.log('Response type:', typeof response);
  console.log('Response keys:', Object.keys(response || {}));

  // Get the template from the response
  const template = response.template || response;

  // Map templateType back to type for frontend compatibility
  if (template) {
    template.type = template.templateType || template.type || 'custom';
    template.htmlContent = template.htmlBody || template.htmlContent;
    template.textContent = template.textBody || template.textContent;
  }

  return template;
});

/**
 * Update an existing email template
 * @param {string} templateId - Template ID
 * @param {Object} updateData - Updated template data
 * @returns {Promise<Object>} Updated template
 */
export const updateEmailTemplate = (templateId, updateData) => handleApiCall(async function updateEmailTemplate() {
  console.log('Updating email template:', templateId, 'with data:', updateData);

  // Extract variables if HTML content is updated
  if (updateData.htmlContent) {
    updateData.variables = extractVariablesFromTemplate(updateData.htmlContent);
  }

  // Convert htmlContent to htmlBody and generate textBody if htmlContent is provided
  if (updateData.htmlContent) {
    updateData.htmlBody = updateData.htmlContent;
    if (!updateData.textBody && !updateData.textContent) {
      updateData.textBody = updateData.htmlContent.replace(/<[^>]*>/g, '').substring(0, 1000) || 'Email content';
    }
  }
  if (updateData.textContent) {
    updateData.textBody = updateData.textContent;
  }

  // Map 'type' to 'templateType' for backend compatibility
  if (updateData.type) {
    updateData.templateType = updateData.type;
    delete updateData.type;
  }

  updateData.updatedAt = new Date().toISOString();

  const response = await put({
    apiName: API_NAME,
    path: `admin/email/templates/${templateId}`,
    options: {
      body: updateData,
      headers: await getAuthHeaders()
    }
  });

  console.log('Raw updateEmailTemplate response:', response);

  // Get the template from the response
  const template = response.template || response;

  // Map templateType back to type for frontend compatibility
  if (template) {
    template.type = template.templateType || template.type || 'custom';
    template.htmlContent = template.htmlBody || template.htmlContent;
    template.textContent = template.textBody || template.textContent;
  }

  return template;
});

/**
 * Delete an email template
 * @param {string} templateId - Template ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteEmailTemplate = (templateId) => handleApiCall(async function deleteEmailTemplate() {
  console.log('Deleting email template:', templateId);

  return await del({
    apiName: API_NAME,
    path: `admin/email/templates/${templateId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Validate email template syntax and variables
 * @param {Object} templateData - Template to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateEmailTemplate = (templateData) => handleApiCall(async function validateEmailTemplate() {
  console.log('Validating email template:', templateData);

  const payload = {
    subject: templateData.subject,
    htmlContent: templateData.htmlContent,
    variables: templateData.variables || []
  };

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/templates/validate',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  return {
    isValid: response.valid,
    errors: response.errors || [],
    warnings: response.warnings || []
  };
});

// ===== EMAIL SENDING =====

/**
 * Send email using a template
 * @param {Object} emailData - Email data (templateId, recipients, variables, etc.)
 * @returns {Promise<Object>} Send result
 */
export const sendTemplateEmail = (emailData) => handleApiCall(async function sendTemplateEmail() {
  console.log('Sending template email:', emailData);

  const requiredFields = ['templateId', 'recipients'];
  for (const field of requiredFields) {
    if (!emailData[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  // Ensure recipients is an array
  const recipients = Array.isArray(emailData.recipients)
    ? emailData.recipients
    : emailData.recipients.split(',').map(email => email.trim());

  const payload = {
    templateId: emailData.templateId,
    recipients,
    variables: emailData.variables || {},
    scheduleFor: emailData.scheduleFor,
    priority: emailData.priority || 'normal',
    metadata: emailData.metadata || {}
  };

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/send/template',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  // Spread the backend response so dryRun results (emails[], totalRecipients)
  // and any future fields flow through to callers; keeps the legacy fields too.
  return {
    success: true,
    emailId: response.emailId,
    messageId: response.messageId,
    recipientCount: recipients.length,
    ...response
  };
});

/**
 * Send custom email (not using template)
 * @param {Object} emailData - Email data (recipients, subject, content, etc.)
 * @returns {Promise<Object>} Send result
 */
export const sendCustomEmail = (emailData) => handleApiCall(async function sendCustomEmail() {
  console.log('📧 FRONTEND: Sending custom email:', emailData);

  const requiredFields = ['recipients', 'subject'];
  for (const field of requiredFields) {
    if (!emailData[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  if (!emailData.htmlContent && !emailData.textContent) {
    throw new Error('Either htmlContent or textContent is required');
  }

  // Ensure recipients is an array
  const recipients = Array.isArray(emailData.recipients)
    ? emailData.recipients
    : emailData.recipients.split(',').map(email => email.trim());

  console.log('📧 FRONTEND: Processed recipients:', recipients);

  const payload = {
    recipients,
    subject: emailData.subject,
    htmlContent: emailData.htmlContent,
    textContent: emailData.textContent,
    variables: emailData.variables || {},
    attachments: emailData.attachments || [],
    scheduleFor: emailData.scheduleFor,
    priority: emailData.priority || 'normal',
    metadata: emailData.metadata || {}
  };

  console.log('📧 FRONTEND: Sending request to API with payload:', payload);

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/send/custom',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  console.log('📧 FRONTEND: Response from send custom email:', response);

  return {
    success: true,
    emailId: response.emailId,
    messageId: response.messageId,
    recipientCount: recipients.length,
    ...response
  };
});

/**
 * Send bulk emails to multiple recipients
 * @param {Object} bulkData - Bulk email data
 * @returns {Promise<Object>} Bulk send result
 */
export const sendBulkEmails = (bulkData) => handleApiCall(async function sendBulkEmails() {
  console.log('Sending bulk emails:', bulkData);

  const requiredFields = ['templateId', 'recipients'];
  for (const field of requiredFields) {
    if (!bulkData[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  const payload = {
    templateId: bulkData.templateId,
    recipients: bulkData.recipients, // Array of {email, variables} objects
    defaultVariables: bulkData.defaultVariables || {},
    scheduleFor: bulkData.scheduleFor,
    priority: bulkData.priority || 'bulk',
    metadata: bulkData.metadata || {}
  };

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/send/bulk',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  return {
    success: true,
    batchId: response.batchId,
    totalEmails: response.totalEmails,
    estimatedDeliveryTime: response.estimatedDeliveryTime
  };
});

/**
 * Send test email
 * @param {string|Object} recipientOrData - Recipient email string or test email data object
 * @returns {Promise<Object>} Test result
 */
export const sendTestEmail = (recipientOrData) => handleApiCall(async function sendTestEmail() {
  console.log('Sending test email:', recipientOrData);

  // Handle both string (simple test) and object (template test) inputs
  let payload;
  let path;

  if (typeof recipientOrData === 'string') {
    // Simple test email without template
    payload = {
      recipient: recipientOrData
    };
    path = 'admin/email/test';
  } else {
    // Template-based test email
    payload = {
      templateId: recipientOrData.templateId,
      recipient: recipientOrData.recipient,
      variables: recipientOrData.variables || {},
      isTest: true
    };
    path = 'admin/email/send/test';
  }

  const response = await post({
    apiName: API_NAME,
    path,
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  return {
    success: true,
    messageId: response.messageId || response.data?.messageId,
    ...response
  };
});

// ===== STUDENT-SPECIFIC EMAIL FUNCTIONS =====

/**
 * Send welcome email to a student
 * @param {string} studentId - Student ID
 * @param {Object} courseData - Course information
 * @returns {Promise<Object>} Send result
 */
export const sendWelcomeEmailToStudent = (studentId, courseData = {}) => handleApiCall(async function sendWelcomeEmailToStudent() {
  console.log('Sending welcome email to student:', studentId);

  const payload = {
    studentId,
    courseData,
    emailType: 'welcome'
  };

  return await post({
    apiName: API_NAME,
    path: `admin/students/${studentId}/email/welcome`,
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send payment reminder to a student
 * @param {string} studentId - Student ID
 * @param {string} bookingId - Booking ID (optional)
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} Send result
 */
export const sendPaymentReminderToStudent = (studentId, bookingId = null, paymentData = {}) => handleApiCall(async function sendPaymentReminderToStudent() {
  console.log('Sending payment reminder to student:', studentId);

  const payload = {
    studentId,
    bookingId,
    paymentData,
    emailType: 'payment_reminder'
  };

  return await post({
    apiName: API_NAME,
    path: 'admin/email/send/student/payment-reminder',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send form completion reminder to a student
 * @param {string} studentId - Student ID
 * @param {string} bookingId - Booking ID
 * @param {Object} formData - Form information
 * @returns {Promise<Object>} Send result
 */
export const sendFormReminderToStudent = (studentId, bookingId, formData = {}) => handleApiCall(async function sendFormReminderToStudent() {
  console.log('Sending form reminder to student:', studentId);

  const payload = {
    studentId,
    bookingId,
    formData,
    emailType: 'form_reminder'
  };

  return await post({
    apiName: API_NAME,
    path: 'admin/email/send/student/form-reminder',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send booking confirmation email
 * @param {string} studentId - Student ID
 * @param {string} bookingId - Booking ID
 * @param {Object} bookingData - Booking details
 * @returns {Promise<Object>} Send result
 */
export const sendBookingConfirmationEmail = (studentId, bookingId, bookingData = {}) => handleApiCall(async function sendBookingConfirmationEmail() {
  console.log('Sending booking confirmation email to student:', studentId);

  const payload = {
    studentId,
    bookingId,
    bookingData,
    emailType: 'booking_confirmation'
  };

  return await post({
    apiName: API_NAME,
    path: 'admin/email/send/student/booking-confirmation',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send invoice email
 * @param {string} studentId - Student ID
 * @param {string} invoiceId - Invoice ID
 * @param {Object} invoiceData - Invoice details
 * @returns {Promise<Object>} Send result
 */
export const sendInvoiceEmail = (studentId, invoiceId, invoiceData = {}) => handleApiCall(async function sendInvoiceEmail() {
  console.log('Sending invoice email to student:', studentId);

  const payload = {
    studentId,
    invoiceId,
    invoiceData,
    emailType: 'invoice'
  };

  return await post({
    apiName: API_NAME,
    path: 'admin/email/send/student/invoice',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

// ===== EMAIL HISTORY & ANALYTICS =====

/**
 * Get email history with filtering and pagination
 * @param {Object} options - Query options (filters, pagination, etc.)
 * @returns {Promise<Object>} Email history with pagination
 */
export const getEmailHistoryArchive = (options = {}) => handleApiCall(async function getEmailHistory() {
  console.log('Fetching email history with options:', options);

  const queryParams = new URLSearchParams();

  // Filters
  if (options.studentId) queryParams.append('studentId', options.studentId);
  if (options.templateId) queryParams.append('templateId', options.templateId);
  if (options.status) queryParams.append('status', options.status);
  if (options.emailType) queryParams.append('emailType', options.emailType);
  if (options.startDate) queryParams.append('startDate', options.startDate);
  if (options.endDate) queryParams.append('endDate', options.endDate);

  // Pagination
  if (options.limit) queryParams.append('limit', options.limit);
  if (options.offset) queryParams.append('offset', options.offset);
  if (options.lastKey) queryParams.append('lastKey', options.lastKey);

  // Sorting
  if (options.sortBy) queryParams.append('sortBy', options.sortBy);
  if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);

  const queryString = queryParams.toString();
  const path = queryString ? `admin/email/history?${queryString}` : 'admin/email/history';

  const response = await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });

  console.log('Raw email history response:', response); // Debug log

  // Handle the nested response structure
  let parsedResponse = response;

  // If response has a nested response property
  if (parsedResponse?.json && typeof parsedResponse.json === 'function') {
    parsedResponse = await parsedResponse.response;
  } else if (parsedResponse?.body?.json && typeof parsedResponse.body.json === 'function') {
    parsedResponse = await parsedResponse.body.json();
  } else if (typeof parsedResponse?.body === 'string') {
    try { parsedResponse = JSON.parse(parsedResponse.body); }
    catch (e) { console.error('Failed to parse email history response body:', e); }
  }



  console.log('Parsed email history response:', parsedResponse);
  console.log('Response emails:', parsedResponse?.emails); // Debug log
  console.log('Response is array?:', Array.isArray(parsedResponse?.emails)); // Debug log

  const emails =
    Array.isArray(parsedResponse?.emails) ? parsedResponse.emails :
      Array.isArray(parsedResponse) ? parsedResponse : [];
  return {
    emails,
    pagination: parsedResponse?.pagination || {},
    totalCount: parsedResponse?.totalCount ?? emails.length ?? 0
  };


});
export const getEmailHistory = (options = {}) => handleApiCall(async function getEmailHistory() {
  console.log('Fetching email history with options:', options);

  // CRITICAL: Manually build query string to preserve + signs in email addresses
  // URLSearchParams interprets + as space, which breaks emails like "user+1@gmail.com"
  const params = [];

  // Filters - email needs encodeURIComponent to preserve + signs
  if (options.studentId)  params.push(`studentId=${encodeURIComponent(options.studentId)}`);
  if (options.templateId) params.push(`templateId=${encodeURIComponent(options.templateId)}`);
  if (options.status)     params.push(`status=${encodeURIComponent(options.status)}`);
  if (options.email)      params.push(`email=${encodeURIComponent(options.email)}`);
  if (options.type)       params.push(`type=${encodeURIComponent(options.type)}`);
  if (options.startDate)  params.push(`startDate=${encodeURIComponent(options.startDate)}`);
  if (options.endDate)    params.push(`endDate=${encodeURIComponent(options.endDate)}`);

  // Pagination
  if (options.limit)   params.push(`limit=${options.limit}`);
  if (options.offset)  params.push(`offset=${options.offset}`);
  if (options.lastKey) params.push(`lastKey=${encodeURIComponent(options.lastKey)}`);

  // Sorting
  if (options.sortBy)    params.push(`sortBy=${encodeURIComponent(options.sortBy)}`);
  if (options.sortOrder) params.push(`sortOrder=${encodeURIComponent(options.sortOrder)}`);

  const queryString = params.join('&');
  const path = queryString ? `admin/email/history?${queryString}` : 'admin/email/history';

  const raw = await get({
    apiName: API_NAME,
    path,
    options: { headers: await getAuthHeaders() }
  });

  console.log('Raw email history response:', raw);

  const parsed = await parseAmplifyRestResult(raw);

  console.log('Parsed email history response:', parsed);
  console.log('Response emails:', parsed?.emails);
  console.log('Response is array?:', Array.isArray(parsed?.emails));

  const emails =
    Array.isArray(parsed?.emails) ? parsed.emails :
    Array.isArray(parsed)         ? parsed :
    [];

  return {
    emails,
    pagination: parsed?.pagination || {},
    totalCount: parsed?.totalCount ?? parsed?.count ?? emails.length ?? 0
  };
});


// Normalizes Amplify REST responses (object, Response, {body: Response}, or stringified JSON)
async function parseAmplifyRestResult(res) {
  let out = res;

  // Unwrap { response: Promise<...> }
  if (out?.response && typeof out.response.then === 'function') {
    out = await out.response;
  }

  // Fetch Response object
  if (out?.json && typeof out.json === 'function') {
    return await out.json();
  }

  // Amplify RestApiResponse: { statusCode, headers, body: Response }
  if (out?.body?.json && typeof out.body.json === 'function') {
    return await out.body.json();
  }

  // Lambda proxy: { body: "json-string" }
  if (typeof out?.body === 'string') {
    try { return JSON.parse(out.body); } catch { /* ignore */ }
  }

  // Raw JSON string
  if (typeof out === 'string') {
    try { return JSON.parse(out); } catch { /* ignore */ }
  }

  // Already parsed object
  return out;
}


/**
 * Get email analytics and statistics
 * @param {Object} options - Analytics options (date range, filters, etc.)
 * @returns {Promise<Object>} Email analytics data
 */
export const getEmailAnalytics = (options = {}) => handleApiCall(async function getEmailAnalytics() {
  console.log('Fetching email analytics with options:', options);

  const queryParams = new URLSearchParams();

  if (options.startDate) queryParams.append('startDate', options.startDate);
  if (options.endDate) queryParams.append('endDate', options.endDate);
  if (options.templateId) queryParams.append('templateId', options.templateId);
  if (options.emailType) queryParams.append('emailType', options.emailType);

  const queryString = queryParams.toString();
  const path = queryString ? `admin/email/analytics?${queryString}` : 'admin/email/analytics';

  const response = await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });

  return {
    totalSent: response.totalSent || 0,
    deliveryRate: response.deliveryRate || 0,
    openRate: response.openRate || 0,
    clickRate: response.clickRate || 0,
    bounceRate: response.bounceRate || 0,
    unsubscribeRate: response.unsubscribeRate || 0,
    topTemplates: response.topTemplates || [],
    dailyStats: response.dailyStats || [],
    statusBreakdown: response.statusBreakdown || {}
  };
});

/**
 * Get email history for a specific student
 * @param {string} studentId - Student ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Student's email history
 */
export const getStudentEmailHistory = (studentId, options = {}) => handleApiCall(async function getStudentEmailHistory() {
  console.log('Fetching email history for student:', studentId);

  const queryParams = new URLSearchParams();

  if (options.limit) queryParams.append('limit', options.limit);
  if (options.offset) queryParams.append('offset', options.offset);
  if (options.emailType) queryParams.append('emailType', options.emailType);
  if (options.startDate) queryParams.append('startDate', options.startDate);
  if (options.endDate) queryParams.append('endDate', options.endDate);

  const queryString = queryParams.toString();
  const path = queryString
    ? `admin/email/students/${studentId}/history?${queryString}`
    : `admin/email/students/${studentId}/history`;

  const response = await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });

  return {
    emails: response.emails || [],
    totalCount: response.totalCount || 0,
    stats: response.stats || {}
  };
});

// ===== TEMPLATE PREVIEW & UTILITIES =====

/**
 * Preview email template with sample data
 * @param {string} templateId - Template ID
 * @param {Object} sampleVariables - Sample variable values
 * @returns {Promise<Object>} Rendered preview
 */
export const previewEmailTemplate = (templateId, sampleVariables = {}) => handleApiCall(async function previewEmailTemplate() {
  console.log('Previewing email template:', templateId);

  const payload = {
    templateId,
    variables: sampleVariables
  };

  const response = await post({
    apiName: API_NAME,
    path: 'admin/email/templates/preview',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });

  return {
    subject: response.subject,
    htmlContent: response.htmlContent,
    textContent: response.textContent,
    variables: response.variables || []
  };
});

/**
 * Get available email variables for templates
 * @param {string} templateType - Template type (welcome, payment_reminder, etc.)
 * @returns {Promise<Array>} Available variables
 */
export const getAvailableVariables = (templateType = null) => handleApiCall(async function getAvailableVariables() {
  console.log('Fetching available variables for type:', templateType);

  const path = templateType
    ? `admin/email/variables?type=${templateType}`
    : 'admin/email/variables';

  const response = await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });

  return response.variables || [];
});

// ===== UTILITY FUNCTIONS =====

/**
 * Extract variables from template content
 * @param {string} content - Template content (HTML or text)
 * @returns {Array} Extracted variable names
 */
export const extractVariablesFromTemplate = (content) => {
  if (!content) return [];

  const variableMatches = content.match(/\{\{([^}]+)\}\}/g) || [];
  const variables = variableMatches.map(match =>
    match.replace(/[{}]/g, '').trim()
  );

  // Remove duplicates and return
  return [...new Set(variables)];
};

/**
 * Validate email address format
 * @param {string} email - Email address
 * @returns {boolean} Valid email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate multiple email addresses
 * @param {Array|string} emails - Email addresses (array or comma-separated string)
 * @returns {Object} Validation result
 */
export const validateEmails = (emails) => {
  const emailList = Array.isArray(emails)
    ? emails
    : emails.split(',').map(email => email.trim());

  const validEmails = [];
  const invalidEmails = [];

  emailList.forEach(email => {
    if (isValidEmail(email)) {
      validEmails.push(email);
    } else {
      invalidEmails.push(email);
    }
  });

  return {
    valid: validEmails,
    invalid: invalidEmails,
    isAllValid: invalidEmails.length === 0
  };
};

/**
 * Format email status for display
 * @param {string} status - Email status
 * @returns {Object} Formatted status with color and label
 */
export const formatEmailStatus = (status) => {
  const statusMap = {
    sent: { label: 'Sent', color: 'blue', description: 'admin/email has been sent' },
    delivered: { label: 'Delivered', color: 'green', description: 'admin/email was delivered successfully' },
    opened: { label: 'Opened', color: 'purple', description: 'admin/email was opened by recipient' },
    clicked: { label: 'Clicked', color: 'indigo', description: 'Links in email were clicked' },
    bounced: { label: 'Bounced', color: 'red', description: 'admin/email bounced and was not delivered' },
    failed: { label: 'Failed', color: 'red', description: 'admin/email failed to send' },
    pending: { label: 'Pending', color: 'yellow', description: 'admin/email is queued for sending' },
    scheduled: { label: 'Scheduled', color: 'gray', description: 'admin/email is scheduled for future delivery' },
    cancelled: { label: 'Cancelled', color: 'gray', description: 'admin/email delivery was cancelled' }
  };

  return statusMap[status] || {
    label: status,
    color: 'gray',
    description: 'Unknown status'
  };
};

/**
 * Calculate email statistics
 * @param {Array} emails - Email records
 * @returns {Object} Calculated statistics
 */
export const calculateEmailStats = (emails) => {
  if (!emails || emails.length === 0) {
    return {
      total: 0,
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      failed: 0,
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      bounceRate: 0
    };
  }

  const stats = emails.reduce((acc, email) => {
    acc.total++;
    if (email.status === 'sent') acc.sent++;
    if (email.status === 'delivered') acc.delivered++;
    if (email.status === 'opened') acc.opened++;
    if (email.status === 'clicked') acc.clicked++;
    if (email.status === 'bounced') acc.bounced++;
    if (email.status === 'failed') acc.failed++;
    return acc;
  }, {
    total: 0, sent: 0, delivered: 0, opened: 0,
    clicked: 0, bounced: 0, failed: 0
  });

  // Calculate rates
  stats.deliveryRate = stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : 0;
  stats.openRate = stats.delivered > 0 ? (stats.opened / stats.delivered) * 100 : 0;
  stats.clickRate = stats.opened > 0 ? (stats.clicked / stats.opened) * 100 : 0;
  stats.bounceRate = stats.sent > 0 ? (stats.bounced / stats.sent) * 100 : 0;

  return stats;
};

const getMockEmailData = (apiName) => {
  const mockData = {
    getEmailTemplates: [
      {
        templateId: 'template-1',
        name: 'Welcome Email',
        type: 'welcome',
        subject: 'Welcome to {{courseName}}!',
        htmlContent: '<h1>Welcome {{studentName}}!</h1><p>Thank you for enrolling in {{courseName}}.</p>',
        variables: ['studentName', 'courseName'],
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        templateId: 'template-2',
        name: 'Payment Reminder',
        type: 'payment_reminder',
        subject: 'Payment reminder for {{courseName}}',
        htmlContent: '<h2>Payment Due</h2><p>Dear {{studentName}}, your payment for {{courseName}} is due on {{dueDate}}.</p>',
        variables: ['studentName', 'courseName', 'dueDate'],
        active: true,
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString()
      },
      {
        templateId: 'template-3',
        name: 'Form Completion Reminder',
        type: 'form_reminder',
        subject: 'Complete your registration for {{courseName}}',
        htmlContent: '<h2>Action Required</h2><p>Hi {{studentName}}, please complete your registration form for {{courseName}}.</p>',
        variables: ['studentName', 'courseName'],
        active: true,
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        updatedAt: new Date(Date.now() - 172800000).toISOString()
      }
    ],

    getEmailTemplate: {
      templateId: 'template-1',
      name: 'Welcome Email',
      type: 'welcome',
      subject: 'Welcome to {{courseName}}!',
      htmlContent: '<h1>Welcome {{studentName}}!</h1><p>Thank you for enrolling in {{courseName}}.</p><p>We look forward to seeing you!</p>',
      variables: ['studentName', 'courseName'],
      active: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },

    createEmailTemplate: {
      templateId: 'template-new',
      name: 'New Template',
      type: 'custom',
      subject: 'Test Subject',
      htmlContent: '<p>Test content</p>',
      variables: [],
      active: true,
      createdAt: new Date().toISOString()
    },

    updateEmailTemplate: {
      templateId: 'template-1',
      name: 'Updated Template',
      type: 'welcome',
      subject: 'Updated Subject',
      htmlContent: '<p>Updated content</p>',
      variables: [],
      active: true,
      updatedAt: new Date().toISOString()
    },

    deleteEmailTemplate: {
      success: true,
      message: 'Template deleted successfully'
    },

    sendTemplateEmail: {
      success: true,
      emailId: 'admin/email-' + Date.now(),
      messageId: 'msg-' + Date.now(),
      recipientCount: 1
    },

    sendCustomEmail: {
      success: true,
      emailId: 'admin/email-' + Date.now(),
      messageId: 'msg-' + Date.now(),
      recipientCount: 1
    },

    sendTestEmail: {
      success: true,
      messageId: 'test-msg-' + Date.now()
    },

    validateEmailTemplate: {
      isValid: true,
      errors: [],
      warnings: []
    },

    getEmailHistory: {
      emails: [
        {
          emailId: 'admin/email-1',
          templateId: 'template-1',
          templateName: 'Welcome Email',
          recipient: 'student@example.com',
          subject: 'Welcome to React Fundamentals!',
          status: 'delivered',
          sentAt: new Date().toISOString()
        },
        {
          emailId: 'admin/email-2',
          templateId: 'template-2',
          templateName: 'Payment Reminder',
          recipient: 'student2@example.com',
          subject: 'Payment reminder for Advanced JavaScript',
          status: 'opened',
          sentAt: new Date(Date.now() - 3600000).toISOString()
        },
        {
          emailId: 'admin/email-3',
          templateId: null,
          templateName: 'Custom',
          recipient: 'student3@example.com',
          subject: 'Course Update',
          status: 'sent',
          sentAt: new Date(Date.now() - 7200000).toISOString()
        }
      ],
      totalCount: 3,
      pagination: {
        hasMore: false,
        offset: 0,
        limit: 50
      }
    },

    getEmailAnalytics: {
      totalSent: 150,
      deliveryRate: 98.5,
      openRate: 65.2,
      clickRate: 12.8,
      bounceRate: 1.5,
      unsubscribeRate: 0.3,
      topTemplates: [
        { templateId: 'template-1', name: 'Welcome Email', sent: 45 },
        { templateId: 'template-2', name: 'Payment Reminder', sent: 32 }
      ],
      dailyStats: [],
      statusBreakdown: {
        sent: 150,
        delivered: 148,
        opened: 96,
        clicked: 19,
        bounced: 2,
        failed: 0
      }
    }
  };

  return mockData[apiName] || { success: true };
};

// ===== EMAIL VARIABLE DIAGNOSTIC =====

/**
 * Diagnose email template variables for a specific email address
 * Shows all bookings and their available template variables
 * @param {string} email - Email address to diagnose
 * @param {string} courseId - Optional course ID filter
 * @param {string} bookingId - Optional specific booking ID
 * @returns {Promise<Object>} Diagnostic result with variables for each booking
 */
export const diagnoseEmailVariables = (email, courseId = null, bookingId = null) => handleApiCall(async function diagnoseEmailVariables() {
  console.log('Diagnosing email variables for:', email || bookingId);

  // CRITICAL: Manually encode the email to preserve + sign
  // URLSearchParams interprets + as space, so we must use encodeURIComponent
  const params = [];
  if (email) params.push(`email=${encodeURIComponent(email)}`);
  if (courseId) params.push(`courseId=${encodeURIComponent(courseId)}`);
  if (bookingId) params.push(`bookingId=${encodeURIComponent(bookingId)}`);

  return await get({
    apiName: API_NAME,
    path: `admin/email/diagnose-variables?${params.join('&')}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

export default {
  // Template Management
  getEmailTemplates,
  getEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  validateEmailTemplate,

  // Email Sending
  sendTemplateEmail,
  sendCustomEmail,
  sendBulkEmails,
  sendTestEmail,

  // Student-Specific Emails
  sendWelcomeEmailToStudent,
  sendPaymentReminderToStudent,
  sendFormReminderToStudent,
  sendBookingConfirmationEmail,
  sendInvoiceEmail,

  // History & Analytics
  getEmailHistory,
  getEmailAnalytics,
  getStudentEmailHistory,

  // Preview & Utilities
  previewEmailTemplate,
  getAvailableVariables,
  extractVariablesFromTemplate,
  isValidEmail,
  validateEmails,
  formatEmailStatus,
  calculateEmailStats,

  // Diagnostics
  diagnoseEmailVariables
};